/**
 * Lightweight per-tab click tracker. Counts every user click on the document
 * and POSTs the count to /api/crm/agent-events/clicks in batches every 30s
 * (and on tab unload via sendBeacon). Agents only — admin clicks are ignored
 * by the backend.
 */
import { useEffect, useRef } from "react";
import { crmApi, auth } from "../../lib/crm";

const FLUSH_INTERVAL_MS = 30_000;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function ClickTracker({ enabled }) {
  const countRef = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;

    const onClick = () => {
      countRef.current += 1;
    };

    const flush = (sync = false) => {
      const n = countRef.current;
      if (n <= 0) return;
      countRef.current = 0;
      if (sync && typeof navigator !== "undefined" && navigator.sendBeacon) {
        try {
          const token = auth.token();
          const blob = new Blob([JSON.stringify({ count: n })], { type: "application/json" });
          // sendBeacon doesn't support custom headers, so we rely on URL-based
          // auth via a query param fallback. The backend accepts both header
          // and query token. Skipping if not available.
          if (token && BACKEND_URL) {
            // Best-effort: queue an XHR with keepalive flag (preferred over Beacon when auth headers needed)
            fetch(`${BACKEND_URL}/api/crm/agent-events/clicks`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ count: n }),
              keepalive: true,
            }).catch(() => {});
          }
          return;
        } catch {
          /* fallthrough */
        }
      }
      crmApi.post("/agent-events/clicks", { count: n }).catch(() => {
        // Re-queue the count so it gets retried on the next flush
        countRef.current += n;
      });
    };

    document.addEventListener("click", onClick, { capture: true });
    const id = setInterval(() => flush(false), FLUSH_INTERVAL_MS);
    const onUnload = () => flush(true);
    window.addEventListener("beforeunload", onUnload);
    window.addEventListener("pagehide", onUnload);

    return () => {
      document.removeEventListener("click", onClick, { capture: true });
      clearInterval(id);
      window.removeEventListener("beforeunload", onUnload);
      window.removeEventListener("pagehide", onUnload);
      flush(true);
    };
  }, [enabled]);

  return null;
}
